import { useEffect, useMemo, useState } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';
import {
  Button,
  EmptyState,
  FilterSelect,
  LoadingState,
  SearchInput,
  TenantSelector,
} from '@/components/ui';
import { mediaAssetsApi } from '@/api/mediaAssets';
import type { MediaAsset } from '@/types/mediaAssets';
import { useTenantScope } from '@/hooks/useTenantScope';
import { useToast } from '@/hooks/useToast';
import { formatDateTime } from '@/utils/formatters';

const publishedOptions = [
  { value: 'published', label: 'Только опубликованные' },
  { value: 'draft', label: 'Только черновики' },
];

// 06.04.26: это самостоятельный tenant directory для media assets.
// Здесь оператор загружает файл, публикует его и получает reusable public URL,
// который потом можно подставлять в OKDoc и другие editor flow без ручного копирования CDN-ссылок.
export function MediaLibraryPage() {
  const { tenantId, isSuperAdmin, selectedTenantId, setSelectedTenant } = useTenantScope();
  const { showError, showSuccess } = useToast();

  const [items, setItems] = useState<MediaAsset[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState('');
  const [publishedFilter, setPublishedFilter] = useState('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadAltText, setUploadAltText] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const publishedOnly = useMemo(() => {
    if (publishedFilter === 'published') return true;
    if (publishedFilter === 'draft') return false;
    return undefined;
  }, [publishedFilter]);

  const loadItems = async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const response = await mediaAssetsApi.list({
        search: search || undefined,
        published_only: publishedOnly,
        limit: 100,
      });
      setItems(response.items || []);
    } catch {
      showError('Не удалось загрузить media library');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadItems();
  }, [tenantId, search, publishedOnly]);

  const handleUpload = async () => {
    if (!uploadFile) {
      showError('Выберите файл для загрузки');
      return;
    }
    setUploading(true);
    try {
      await mediaAssetsApi.upload({
        file: uploadFile,
        title: uploadTitle || uploadFile.name,
        alt_text: uploadAltText,
        publish: true,
      });
      showSuccess('Файл загружен в gallery');
      setUploadFile(null);
      setUploadTitle('');
      setUploadAltText('');
      const input = document.getElementById('media-library-upload') as HTMLInputElement | null;
      if (input) input.value = '';
      await loadItems();
    } catch {
      showError('Не удалось загрузить файл');
    } finally {
      setUploading(false);
    }
  };

  const togglePublish = async (item: MediaAsset) => {
    try {
      await mediaAssetsApi.update(item.id, {
        title: item.title || item.original_filename,
        alt_text: item.alt_text || '',
        description: item.description || '',
        publish: !item.is_published,
        lifecycle_state: item.lifecycle_state || 'active',
      });
      showSuccess(!item.is_published ? 'Asset опубликован' : 'Asset снят с публикации');
      await loadItems();
    } catch {
      showError('Не удалось обновить publish state');
    }
  };

  const handleDelete = async (item: MediaAsset) => {
    if (!window.confirm(`Удалить asset "${item.title || item.original_filename}"?`)) {
      return;
    }
    try {
      await mediaAssetsApi.remove(item.id);
      showSuccess('Asset удалён');
      await loadItems();
    } catch {
      showError('Не удалось удалить asset');
    }
  };

  const copyPublicURL = async (item: MediaAsset) => {
    if (!item.public_url) {
      showError('У asset пока нет public URL');
      return;
    }
    try {
      await navigator.clipboard.writeText(item.public_url);
      showSuccess('Ссылка скопирована');
    } catch {
      showError('Не удалось скопировать ссылку');
    }
  };

  const selectedItem = items.find((item) => item.id === selectedId) ?? null;

  return (
    <MainLayout
      headerTitle="Галерея медиа"
      headerActions={
        isSuperAdmin ? (
          <TenantSelector
            value={selectedTenantId || ''}
            onChange={setSelectedTenant}
            required={false}
            showLabel={false}
          />
        ) : undefined
      }
    >
      <div className="space-y-3 p-4">
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-shell-border bg-shell-surface px-3 py-2">
          <input
            id="media-library-upload"
            type="file"
            onChange={(event) => setUploadFile(event.target.files?.[0] || null)}
            className="block min-w-[180px] flex-1 text-sm text-shell-text"
          />
          <input
            value={uploadTitle}
            onChange={(event) => setUploadTitle(event.target.value)}
            placeholder="Название"
            className="h-9 min-w-[140px] rounded-lg border border-shell-border bg-shell-bg px-3 text-sm"
          />
          <input
            value={uploadAltText}
            onChange={(event) => setUploadAltText(event.target.value)}
            placeholder="Alt text"
            className="h-9 min-w-[140px] rounded-lg border border-shell-border bg-shell-bg px-3 text-sm"
          />
          <Button onClick={handleUpload} isLoading={uploading}>Загрузить</Button>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="min-w-[220px] flex-1">
            <SearchInput value={search} onChange={setSearch} placeholder="Поиск по названию, alt или файлу" />
          </div>
          <div className="w-56">
            <FilterSelect value={publishedFilter} onChange={setPublishedFilter} options={publishedOptions} placeholder="Все asset" />
          </div>
        </div>

        {selectedItem && (
          <div className="flex flex-wrap items-start justify-between gap-3 rounded-xl border border-shell-border bg-shell-surface px-4 py-3">
            <div className="min-w-0">
              {selectedItem.media_kind === 'image' && selectedItem.public_url ? (
                <img src={selectedItem.public_url} alt={selectedItem.alt_text || selectedItem.title || selectedItem.original_filename} className="mb-3 h-40 rounded-lg object-cover" />
              ) : null}
              {selectedItem.media_kind === 'video' && selectedItem.public_url ? (
                <video src={selectedItem.public_url} controls className="mb-3 h-40 rounded-lg bg-black" />
              ) : null}
              <p className="text-sm font-semibold text-shell-strong">{selectedItem.title || selectedItem.original_filename}</p>
              <p className="mt-1 text-xs leading-4 text-shell-muted">
                {selectedItem.mime_type} · {Math.ceil(selectedItem.file_size / 1024)} KB · {selectedItem.is_published ? 'Опубликован' : 'Черновик'} · {formatDateTime(selectedItem.updated_at)}
              </p>
              <p className="mt-2 break-all text-xs leading-4 text-shell-text">{selectedItem.public_url || 'Публичной ссылки ещё нет.'}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="secondary" size="sm" onClick={() => togglePublish(selectedItem)}>
                {selectedItem.is_published ? 'Снять с публикации' : 'Опубликовать'}
              </Button>
              <Button variant="secondary" size="sm" onClick={() => copyPublicURL(selectedItem)} disabled={!selectedItem.public_url}>
                Копировать ссылку
              </Button>
              <Button variant="danger" size="sm" onClick={() => handleDelete(selectedItem)}>Удалить</Button>
            </div>
          </div>
        )}

        {loading ? (
          <LoadingState />
        ) : items.length === 0 ? (
          <EmptyState type="noData" title="Галерея пуста" description="Загрузите изображение или видео для этой клиники." />
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5">
            {items.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setSelectedId(item.id)}
                className={`overflow-hidden rounded-xl border text-left ${item.id === selectedId ? 'border-shell-primary bg-shell-primary-soft' : 'border-shell-border bg-shell-surface'}`}
              >
                {item.media_kind === 'image' && item.public_url ? (
                  <img src={item.public_url} alt={item.alt_text || item.title || item.original_filename} className="h-28 w-full object-cover" />
                ) : (
                  <div className="flex h-28 items-center justify-center bg-shell-bg text-xs text-shell-muted">{item.media_kind.toUpperCase()}</div>
                )}
                <div className="px-2 py-2">
                  <p className="truncate text-sm font-medium text-shell-strong">{item.title || item.original_filename}</p>
                  <p className="text-xs text-shell-muted">{item.is_published ? 'Опубликован' : 'Черновик'}</p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </MainLayout>
  );
}
